import type { FC, FormEvent } from 'react'
import type { CartLine, PickupAvailability } from '@/types'

import Link from 'next/link'
import { CircleAlert, LoaderCircle, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatMoney, toCents } from '@/lib/format'
import { Button } from '@/components/generals/button'
import { QuantityStepper } from '@/components/generals/quantity-stepper'
import { PickupPicker } from '@/components/order/pickup-picker'

export type OrderFields = {
    name: string
    phone: string
    note: string
    website: string
}

interface OrderSummaryProps {
    idPrefix: string
    lines: Array<CartLine>
    total: number
    onQuantity: (line: CartLine, quantity: number) => void
    onRemove: (slug: string) => void
    availability: PickupAvailability | null
    availabilityState: 'loading' | 'ready' | 'error'
    date: string | null
    time: string | null
    onDateChange: (date: string) => void
    onTimeChange: (time: string) => void
    onRetry: () => void
    fields: OrderFields
    onField: (field: keyof OrderFields, value: string) => void
    errors: Record<string, string>
    formError: string | null
    warning: string | null
    submitting: boolean
    onSubmit: (event: FormEvent<HTMLFormElement>) => void
    showTitle?: boolean
}

const inputClasses = (invalid: boolean): string => cn(
    'w-full rounded-sm border bg-background px-4 text-[0.9375rem] transition-colors duration-300 placeholder:text-crust-soft/70 focus:border-foreground focus:outline-none',
    invalid ? 'border-destructive' : 'border-line-strong hover:border-foreground/60'
)

export const OrderSummary: FC<OrderSummaryProps> = ({
    idPrefix,
    lines,
    total,
    onQuantity,
    onRemove,
    availability,
    availabilityState,
    date,
    time,
    onDateChange,
    onTimeChange,
    onRetry,
    fields,
    onField,
    errors,
    formError,
    warning,
    submitting,
    onSubmit,
    showTitle = true
}) => {
    const empty = lines.length === 0

    return (
        <form onSubmit={onSubmit} noValidate aria-label='سفارش شما'>
            {showTitle && (
                <h2 className='font-heading text-[2rem] leading-none font-normal'>سفارش شما</h2>
            )}

            {empty ? (
                <div className={cn('border-y border-line py-8 text-center', showTitle && 'mt-6')}>
                    <p className='font-heading text-2xl'>هنوز چیزی این‌جا نیست.</p>
                    <p className='mx-auto mt-2 max-w-xs text-sm leading-relaxed text-crust-soft'>
                        یکی دو قرص نان از فهرست اضافه کنید تا سفارشتان این‌جا شکل بگیرد.
                    </p>
                </div>
            ) : (
                <ul className={cn('border-t border-line', showTitle && 'mt-6')} aria-label='اقلام سفارش شما'>
                    {lines.map(line => (
                        <li key={line.slug} className='flex items-center gap-3 border-b border-line py-3.5'>
                            <div className='min-w-0 flex-1'>
                                <p className='truncate text-[0.9375rem] font-medium'>{line.name}</p>
                                <p className='text-sm text-crust-soft tabular-nums'>{formatMoney(toCents(line.price) * line.quantity / 100)}</p>
                            </div>

                            <QuantityStepper value={line.quantity} onChange={quantity => onQuantity(line, quantity)} min={1} size='sm' label={line.name} />

                            <button
                                type='button'
                                onClick={() => onRemove(line.slug)}
                                aria-label={`حذف ${line.name} از سفارش`}
                                className='inline-flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-sm text-crust-soft transition-colors duration-300 hover:bg-flour-deep hover:text-foreground focus-visible:outline-2 focus-visible:outline-amber'
                            >
                                <X className='size-4' aria-hidden='true' />
                            </button>
                        </li>
                    ))}
                </ul>
            )}

            <div className='flex items-baseline justify-between gap-4 pt-4'>
                <p className='text-[0.9375rem]'>جمع کل</p>
                <p className='font-heading text-[2rem] leading-none tabular-nums'>{formatMoney(total)}</p>
            </div>

            <p className='mt-1 text-end text-xs text-crust-soft'>پرداخت هنگام تحویل. هیچ مبلغی آنلاین دریافت نمی‌شود.</p>

            {errors.items && (
                <p className='mt-4 flex gap-2 text-sm text-destructive' role='alert'>
                    <CircleAlert className='mt-0.5 size-4 shrink-0' aria-hidden='true' />
                    {errors.items}
                </p>
            )}

            {warning && !errors.items && (
                <p className='mt-4 flex gap-2 border-s-2 border-amber ps-3 text-sm leading-relaxed text-foreground' role='status'>
                    {warning}
                </p>
            )}

            <div className='mt-8 border-t border-line pt-7'>
                <PickupPicker
                    idPrefix={idPrefix}
                    availability={availability}
                    state={availabilityState}
                    date={date}
                    time={time}
                    onDateChange={onDateChange}
                    onTimeChange={onTimeChange}
                    onRetry={onRetry}
                    dateError={errors.pickupDate}
                    timeError={errors.pickupTime}
                />
            </div>

            <div className='mt-8 space-y-5 border-t border-line pt-7'>
                <p className='label-caps text-crust-soft'>مشخصات شما</p>

                <div>
                    <label htmlFor={`${idPrefix}-name`} className='text-sm font-medium'>نام سفارش‌دهنده</label>
                    <input
                        id={`${idPrefix}-name`}
                        name='name'
                        type='text'
                        autoComplete='name'
                        value={fields.name}
                        onChange={event => onField('name', event.target.value)}
                        aria-invalid={errors.name ? true : undefined}
                        aria-describedby={errors.name ? `${idPrefix}-name-error` : undefined}
                        className={cn(inputClasses(Boolean(errors.name)), 'mt-2 h-12')}
                        maxLength={80}
                    />
                    {errors.name && <p id={`${idPrefix}-name-error`} className='mt-1.5 text-sm text-destructive'>{errors.name}</p>}
                </div>

                <div>
                    <label htmlFor={`${idPrefix}-phone`} className='text-sm font-medium'>تلفن</label>
                    <input
                        id={`${idPrefix}-phone`}
                        name='phone'
                        type='tel'
                        autoComplete='tel'
                        inputMode='tel'
                        value={fields.phone}
                        onChange={event => onField('phone', event.target.value)}
                        aria-invalid={errors.phone ? true : undefined}
                        aria-describedby={`${idPrefix}-phone-hint${errors.phone ? ` ${idPrefix}-phone-error` : ''}`}
                        className={cn(inputClasses(Boolean(errors.phone)), 'mt-2 h-12')}
                        maxLength={24}
                    />
                    <p id={`${idPrefix}-phone-hint`} className='mt-1.5 text-xs text-crust-soft'>فقط اگر لازم باشد درباره‌ی این سفارش با شما تماس بگیریم، از آن استفاده می‌کنیم.</p>
                    {errors.phone && <p id={`${idPrefix}-phone-error`} className='mt-1 text-sm text-destructive'>{errors.phone}</p>}
                </div>

                <div>
                    <label htmlFor={`${idPrefix}-note`} className='text-sm font-medium'>
                        یادداشت <span className='font-normal text-crust-soft'>(اختیاری)</span>
                    </label>
                    <textarea
                        id={`${idPrefix}-note`}
                        name='note'
                        rows={3}
                        value={fields.note}
                        onChange={event => onField('note', event.target.value)}
                        placeholder='نان برش‌خورده، شمع تولد، یا هر چیزی که باید بدانیم'
                        className={cn(inputClasses(false), 'mt-2 resize-y py-3 leading-relaxed')}
                        maxLength={500}
                    />
                </div>

                <div className='sr-only' aria-hidden='true'>
                    <label htmlFor={`${idPrefix}-website`}>این فیلد را خالی بگذارید</label>
                    <input id={`${idPrefix}-website`} name='website' type='text' tabIndex={-1} autoComplete='off' value={fields.website} onChange={event => onField('website', event.target.value)} />
                </div>
            </div>

            {formError && (
                <p className='mt-6 flex gap-2 border-s-2 border-destructive ps-3 text-sm leading-relaxed text-destructive' role='alert'>
                    {formError}
                </p>
            )}

            <Button type='submit' size='lg' className='mt-7 w-full' disabled={submitting || empty}>
                {submitting ? (
                    <>
                        <LoaderCircle className='size-4 animate-spin' aria-hidden='true' />
                        در حال ثبت سفارش
                    </>
                ) : (
                    <>
                        ثبت سفارش
                        {!empty && <span className='tabular-nums opacity-80'>{formatMoney(total)}</span>}
                    </>
                )}
            </Button>

            <p className='mt-4 text-center text-xs leading-relaxed text-crust-soft'>
                درباره‌ی سفارش بزرگ یا خاص سؤالی دارید؟ <Link href='/visit#faq' className='underline decoration-line-strong underline-offset-4 hover:decoration-foreground'>پرسش‌های پرتکرار</Link> را بخوانید یا با ما تماس بگیرید.
            </p>
        </form>
    )
}
